import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Button,
    Card,
    Form,
    Alert,
    Input,
    Modal,
    Popconfirm,
    Row,
    Select,
    Space,
    Table,
    Tabs,
    Tag,
    Typography,
    message
} from 'antd';
import {
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    StopOutlined
} from '@ant-design/icons';
import { connect } from 'react-redux';
import {
    deleteGlobalDocumentType,
    getGlobalDocumentTypes,
    getProcedureDocumentTemplateTags,
    getGlobalPhysicalSpaceTypes,
    inactivateGlobalPhysicalSpaceType,
    postGlobalDocumentType,
    postGlobalPhysicalSpaceType,
    putGlobalDocumentType,
    putGlobalPhysicalSpaceType
} from '../store/user/thunks';
import GlobalAccessConfiguration from './GlobalAccessConfiguration';

const { Text } = Typography;

const TEMPLATE_EXAMPLE = `<div style="font-family: Arial; font-size: 14px; color: #1f2937;">
  <h2 style="text-align:center;">REPORTE DE LABORATORIO</h2>
  <p><strong>Paciente:</strong> {{PACIENTE_NOMBRE}}</p>
  <p><strong>Cédula:</strong> {{PACIENTE_IDENTIFICACION}}</p>
  <p><strong>Historia clínica:</strong> {{HISTORIA_CLINICA}}</p>
  <p><strong>Fecha:</strong> {{FECHA_EMISION}}</p>
  <p><strong>Médico solicitante:</strong> {{MEDICO_NOMBRE}}</p>
  <p><strong>Examen:</strong> {{PRODUCTO_NOMBRE}}</p>
  <h3>Resultado</h3>
  <table border="1" width="100%" cellpadding="6" style="border-collapse: collapse;">
    <tr><th>Parámetro</th><th>Resultado</th></tr>
    <tr><td>Paratífico A</td><td data-field="PARATIFICO_A"></td></tr>
    <tr><td>Paratífico B</td><td data-field="PARATIFICO_B"></td></tr>
  </table>
  <p><strong>Observaciones:</strong></p>
  <div data-field="OBSERVACIONES"></div>
  <p><strong>Firma responsable:</strong></p>
  <div data-field="FIRMA_MEDICO"></div>
</div>`;

const PROCEDURE_REPORT_TEMPLATE = `<div style="font-family: Arial; font-size: 13px; color: #1f2937; line-height: 1.45;">
  <div style="text-align:center; border-bottom:2px solid #1f2937; padding-bottom:10px;">
    <h2 style="margin:0;">INFORME DE PROCEDIMIENTO</h2>
    <p style="margin:4px 0;">{{PRODUCTO_NOMBRE}}</p>
  </div>

  <table width="100%" cellpadding="5" style="margin-top:12px; border-collapse:collapse;">
    <tr><td><strong>Paciente:</strong> {{PACIENTE_NOMBRE}}</td><td><strong>Cédula:</strong> {{PACIENTE_IDENTIFICACION}}</td></tr>
    <tr><td><strong>Historia clínica:</strong> {{HISTORIA_CLINICA}}</td><td><strong>Código atención:</strong> {{ATENCION_CODIGO}}</td></tr>
    <tr><td><strong>Fecha atención:</strong> {{ATENCION_FECHA}}</td><td><strong>Fecha emisión:</strong> {{FECHA_EMISION}}</td></tr>
    <tr><td><strong>Fecha nacimiento:</strong> {{PACIENTE_FECHA_NACIMIENTO}}</td><td><strong>Género:</strong> {{PACIENTE_GENERO}}</td></tr>
    <tr><td><strong>Médico responsable:</strong> {{MEDICO_NOMBRE}}</td><td><strong>Especialidad:</strong> {{MEDICO_ESPECIALIDAD}}</td></tr>
    <tr><td><strong>Centro:</strong> {{CENTRO_NOMBRE}}</td><td><strong>Procedimiento:</strong> {{PRODUCTO_NOMBRE}}</td></tr>
  </table>

  <h3>1. Diagnóstico e indicación</h3>
  <p><strong>Diagnósticos registrados en la ficha médica:</strong></p>
  <div>{{DIAGNOSTICOS}}</div>
  <p><strong>Órdenes médicas:</strong> {{ORDENES_MEDICAS}}</p>
  <p><strong>Signos vitales registrados:</strong> {{SIGNOS_VITALES}}</p>
  <p><strong>Resultados previos:</strong> {{RESULTADOS_PREVIOS}}</p>
  <p><strong>Indicación:</strong></p>
  <div data-field="INDICACION"></div>

  <h3>2. Descripción del procedimiento</h3>
  <p><strong>Técnica utilizada:</strong></p>
  <div data-field="TECNICA"></div>
  <p><strong>Hallazgos:</strong></p>
  <div data-field="HALLAZGOS"></div>

  <h3>3. Resultado y novedades</h3>
  <p><strong>Resultado:</strong></p>
  <div data-field="RESULTADO"></div>
  <p><strong>Complicaciones:</strong></p>
  <div data-field="COMPLICACIONES"></div>
  <p><strong>Muestras o elementos enviados:</strong></p>
  <div data-field="MUESTRAS"></div>

  <h3>4. Recomendaciones</h3>
  <div data-field="RECOMENDACIONES"></div>

  <table width="100%" cellpadding="5" style="margin-top:32px;">
    <tr><td style="text-align:center;">____________________________<br/>Firma del médico</td><td style="text-align:center;">____________________________<br/>Firma del paciente</td></tr>
  </table>
</div>`;

const renderTemplatePreview = (template: string) => {
    const values: Record<string, string> = {
        PACIENTE_NOMBRE: 'Paciente de ejemplo',
        PACIENTE_IDENTIFICACION: '9999999999',
        HISTORIA_CLINICA: 'HC-DEMO-0001',
        PACIENTE_FECHA_NACIMIENTO: '01/01/1985',
        PACIENTE_GENERO: 'No especificado',
        ATENCION_CODIGO: 'AT-DEMO-0001',
        ATENCION_FECHA: '01/01/2026 09:30',
        FECHA_EMISION: '01/01/2026',
        MEDICO_NOMBRE: 'Dr./Dra. de ejemplo',
        MEDICO_ESPECIALIDAD: 'Especialidad de ejemplo',
        CENTRO_NOMBRE: 'Centro de ejemplo',
        PRODUCTO_NOMBRE: 'Procedimiento de ejemplo',
        DIAGNOSTICOS: 'Diagnóstico proveniente de la ficha médica',
        ORDENES_MEDICAS: 'Orden médica de ejemplo',
        SIGNOS_VITALES: 'PA 120/80 mmHg · FC 72 lpm · Temp. 36.5 °C',
        RESULTADOS_PREVIOS: 'Sin resultados previos'
    };

    let html = template || '<div style="padding: 24px; color: #6b7280;">Escriba una plantilla HTML para ver la vista previa.</div>';
    html = html.replace(/<script[\s\S]*?<\/script>/gi, '');
    html = html.replace(/\son[a-z]+\s*=\s*(["']).*?\1/gi, '');
    html = html.replace(/\{\{([A-Z0-9_]+)\}\}/g, (_match, key: string) => values[key] || `[${key}]`);
    html = html.replace(/data-field="([^"]+)"/gi, 'data-preview-field="$1"');
    html = html.replace(/(<(?:div|span|td|p)[^>]*data-preview-field="([^"]+)"[^>]*)>(\s*<\/[^>]+>|\s*)/gi, (_match, opening: string, key: string) => `${opening}<span style="display:inline-block;min-width:120px;min-height:22px;padding:2px 6px;border:1px dashed #1677ff;background:#e6f4ff;color:#0958d9;">Campo editable: ${key}</span>`);
    return `<!doctype html><html><head><meta charset="utf-8"><style>body{margin:24px;background:white;}table{max-width:100%;}img{max-width:100%;}</style></head><body>${html}</body></html>`;
};

type AnyRecord = Record<string, any>;

interface GlobalOrganizationConfigurationProps {
    /**
     * Permite reutilizar esta pantalla como una pestaña del centro de
     * configuración. `all` conserva la ruta directa existente.
     */
    section?: 'all' | 'access' | 'spaces' | 'documents';
    getGlobalPhysicalSpaceTypes?: any;
    postGlobalPhysicalSpaceType?: any;
    putGlobalPhysicalSpaceType?: any;
    inactivateGlobalPhysicalSpaceType?: any;
    getGlobalDocumentTypes?: any;
    getProcedureDocumentTemplateTags?: any;
    postGlobalDocumentType?: any;
    putGlobalDocumentType?: any;
    deleteGlobalDocumentType?: any;
}

const pick = (source: AnyRecord | null | undefined, ...keys: string[]) => {
    if (!source) return undefined;
    for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null) return source[key];
    }
    return undefined;
};

const normalizeList = (source: any): AnyRecord[] => {
    if (Array.isArray(source)) return source;
    const list = pick(source, 'data', 'Data', 'items', 'Items');
    return Array.isArray(list) ? list : [];
};

const normalizeTemplateTags = (source: any): AnyRecord[] => {
    const tags = normalizeList(source)
        .map(item => ({
            code: String(pick(item, 'code', 'Code') || '').trim(),
            label: String(pick(item, 'label', 'Label') || '').trim(),
            token: pick(item, 'token', 'Token'),
            category: String(pick(item, 'category', 'Category') || '').trim(),
            type: String(pick(item, 'type', 'Type') || '').trim().toUpperCase(),
            example: pick(item, 'example', 'Example')
        }))
        .filter(item => item.code && item.label && (item.type === 'VARIABLE' || item.type === 'EDITABLE_FIELD'));

    return tags;
};

const GlobalOrganizationConfiguration: React.FC<GlobalOrganizationConfigurationProps> = props => {
    const {
        section = 'all',
        getGlobalPhysicalSpaceTypes,
        postGlobalPhysicalSpaceType,
        putGlobalPhysicalSpaceType,
        inactivateGlobalPhysicalSpaceType,
        getGlobalDocumentTypes,
        getProcedureDocumentTemplateTags,
        postGlobalDocumentType,
        putGlobalDocumentType,
        deleteGlobalDocumentType
    } = props;
    const [messageApi, contextHolder] = message.useMessage();
    const [spaceTypes, setSpaceTypes] = useState<AnyRecord[]>([]);
    const [documentTypes, setDocumentTypes] = useState<AnyRecord[]>([]);
    const [templateTags, setTemplateTags] = useState<AnyRecord[]>([]);
    const [spaceModalOpen, setSpaceModalOpen] = useState(false);
    const [documentModalOpen, setDocumentModalOpen] = useState(false);
    const [editingSpace, setEditingSpace] = useState<AnyRecord | null>(null);
    const [editingDocument, setEditingDocument] = useState<AnyRecord | null>(null);
    const [spaceName, setSpaceName] = useState('');
    const [spaceObservation, setSpaceObservation] = useState('');
    const [spaceStatus, setSpaceStatus] = useState('ACTIVO');
    const [documentName, setDocumentName] = useState('');
    const [documentCode, setDocumentCode] = useState('');
    const [documentTemplate, setDocumentTemplate] = useState('');
    const templateTextAreaRef = useRef<any>(null);
    const [loading, setLoading] = useState(false);

    const loadCatalogs = useCallback(async () => {
        setLoading(true);
        try {
            const tagsPromise = Promise.resolve(getProcedureDocumentTemplateTags?.()).catch(() => undefined);
            const [spaces, documents, tags] = await Promise.all([
                getGlobalPhysicalSpaceTypes?.(),
                getGlobalDocumentTypes?.(),
                tagsPromise
            ]);
            setSpaceTypes(normalizeList(spaces));
            setDocumentTypes(normalizeList(documents));
            setTemplateTags(normalizeTemplateTags(tags));
        } finally {
            setLoading(false);
        }
    }, [getGlobalDocumentTypes, getGlobalPhysicalSpaceTypes, getProcedureDocumentTemplateTags]);

    useEffect(() => {
        loadCatalogs();
    }, [loadCatalogs]);

    const openSpaceModal = (item?: AnyRecord) => {
        setEditingSpace(item || null);
        setSpaceName(String(pick(item, 'name', 'Name') || ''));
        setSpaceObservation(String(pick(item, 'observation', 'Observation') || ''));
        setSpaceStatus(String(pick(item, 'status', 'Status') || 'ACTIVO').toUpperCase());
        setSpaceModalOpen(true);
    };

    const openDocumentModal = (item?: AnyRecord) => {
        setEditingDocument(item || null);
        setDocumentName(String(pick(item, 'name', 'Name') || ''));
        setDocumentCode(String(pick(item, 'code', 'Code') || ''));
        setDocumentTemplate(String(pick(item, 'template', 'Template') || ''));
        setDocumentModalOpen(true);
    };

    const saveSpace = async () => {
        if (!spaceName.trim()) return;
        const payload = {
            name: spaceName.trim(),
            observation: spaceObservation.trim() || null,
            status: spaceStatus
        };
        const response = editingSpace
            ? await putGlobalPhysicalSpaceType?.(pick(editingSpace, 'id', 'Id'), payload)
            : await postGlobalPhysicalSpaceType?.(payload);
        setSpaceTypes(normalizeList(response));
        setSpaceModalOpen(false);
        messageApi.success(editingSpace ? 'Tipo de espacio actualizado.' : 'Tipo de espacio creado.');
    };

    const inactivateSpace = async (id: string) => {
        const response = await inactivateGlobalPhysicalSpaceType?.(id);
        setSpaceTypes(normalizeList(response));
        messageApi.success('Tipo de espacio inactivado.');
    };

    const saveDocument = async () => {
        if (!documentName.trim() || !documentCode.trim()) return;
        const payload = {
            name: documentName.trim(),
            code: documentCode.trim(),
            template: documentTemplate.trim() || null
        };
        const response = editingDocument
            ? await putGlobalDocumentType?.(pick(editingDocument, 'id', 'Id'), payload)
            : await postGlobalDocumentType?.(payload);
        setDocumentTypes(normalizeList(response));
        setDocumentModalOpen(false);
        messageApi.success(editingDocument ? 'Tipo de documento actualizado.' : 'Tipo de documento creado.');
    };

    const insertTemplateText = (value: string) => {
        const textarea = templateTextAreaRef.current?.resizableTextArea?.textArea as HTMLTextAreaElement | undefined;
        const start = textarea?.selectionStart ?? documentTemplate.length;
        const end = textarea?.selectionEnd ?? documentTemplate.length;
        const nextValue = `${documentTemplate.slice(0, start)}${value}${documentTemplate.slice(end)}`;
        setDocumentTemplate(nextValue);
        window.setTimeout(() => {
            textarea?.focus();
            const cursor = start + value.length;
            textarea?.setSelectionRange(cursor, cursor);
        }, 0);
    };

    const removeDocument = async (id: string) => {
        const response = await deleteGlobalDocumentType?.(id);
        setDocumentTypes(normalizeList(response));
        messageApi.success('Tipo de documento eliminado.');
    };

    const spaceColumns = [
        {
            title: 'Nombre',
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: 'Observacion',
            dataIndex: 'observation',
            key: 'observation',
            render: (value: string) => value || <Text type="secondary">Sin observacion</Text>
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            render: (value: string) => <Tag color={value === 'ACTIVO' ? 'green' : 'default'}>{value}</Tag>
        },
        {
            title: 'Acciones',
            key: 'actions',
            render: (_: unknown, record: AnyRecord) => <Space>
                <Button type="text" icon={<EditOutlined />} onClick={() => openSpaceModal(record)} aria-label="Editar tipo de espacio" />
                {String(pick(record, 'status', 'Status')).toUpperCase() === 'ACTIVO' && <Popconfirm
                    title="Inactivar este tipo de espacio?"
                    onConfirm={() => inactivateSpace(String(pick(record, 'id', 'Id')))}
                    okText="Inactivar"
                    cancelText="Cancelar"
                >
                    <Button type="text" danger icon={<StopOutlined />} aria-label="Inactivar tipo de espacio" />
                </Popconfirm>}
            </Space>
        }
    ];

    const documentColumns = [
        { title: 'Nombre', dataIndex: 'name', key: 'name' },
        { title: 'Codigo', dataIndex: 'code', key: 'code' },
        {
            title: 'Plantilla',
            dataIndex: 'template',
            key: 'template',
            render: (value: string) => value ? 'Configurada' : <Text type="secondary">Sin plantilla</Text>
        },
        {
            title: 'Acciones',
            key: 'actions',
            render: (_: unknown, record: AnyRecord) => <Space>
                <Button type="text" icon={<EditOutlined />} onClick={() => openDocumentModal(record)} aria-label="Editar tipo de documento" />
                <Popconfirm
                    title="Eliminar este tipo de documento?"
                    description="Solo se eliminara si ningun documento lo utiliza."
                    onConfirm={() => removeDocument(String(pick(record, 'id', 'Id')))}
                    okText="Eliminar"
                    cancelText="Cancelar"
                >
                    <Button type="text" danger icon={<DeleteOutlined />} aria-label="Eliminar tipo de documento" />
                </Popconfirm>
            </Space>
        }
    ];

    const automaticTemplateTags = templateTags.filter(item => item.type === 'VARIABLE');
    const editableTemplateTags = templateTags.filter(item => item.type === 'EDITABLE_FIELD');

    const configurationTabs = [
        {
            key: 'access-configuration',
            label: 'Sistemas, módulos y roles',
            children: <GlobalAccessConfiguration />
        },
        {
            key: 'space-types',
            label: 'Tipos de espacio fisico',
            children: <>
                <Row justify="end" style={{ marginBottom: 16 }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => openSpaceModal()}>Nuevo tipo</Button>
                </Row>
                <Table rowKey={(record: AnyRecord) => String(pick(record, 'id', 'Id'))} columns={spaceColumns} dataSource={spaceTypes} pagination={{ pageSize: 8 }} />
            </>
        },
        {
            key: 'document-types',
            label: 'Tipos de documento',
            children: <>
                <Row justify="end" style={{ marginBottom: 16 }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => openDocumentModal()}>Nuevo tipo</Button>
                </Row>
                <Table rowKey={(record: AnyRecord) => String(pick(record, 'id', 'Id'))} columns={documentColumns} dataSource={documentTypes} pagination={{ pageSize: 8 }} />
            </>
        }
    ];

    const sectionKeys: Record<NonNullable<GlobalOrganizationConfigurationProps['section']>, string[]> = {
        all: ['access-configuration', 'space-types', 'document-types'],
        access: ['access-configuration'],
        spaces: ['space-types'],
        documents: ['document-types']
    };

    return <>
        {contextHolder}
        <Card title="Configuración global" loading={loading}>
            <Tabs items={configurationTabs.filter(item => sectionKeys[section].includes(item.key))} />
        </Card>

        <Modal
            title={editingSpace ? 'Editar tipo de espacio fisico' : 'Nuevo tipo de espacio fisico'}
            open={spaceModalOpen}
            onCancel={() => setSpaceModalOpen(false)}
            onOk={saveSpace}
            okText="Guardar"
            cancelText="Cancelar"
        >
            <Form layout="vertical">
                <Form.Item label="Nombre" required>
                    <Input value={spaceName} maxLength={255} onChange={event => setSpaceName(event.target.value)} />
                </Form.Item>
                <Form.Item label="Observacion">
                    <Input.TextArea value={spaceObservation} maxLength={255} rows={3} onChange={event => setSpaceObservation(event.target.value)} />
                </Form.Item>
                <Form.Item label="Estado">
                    <Select value={spaceStatus} onChange={setSpaceStatus} options={[
                        { value: 'ACTIVO', label: 'Activo' },
                        { value: 'INACTIVO', label: 'Inactivo' }
                    ]} />
                </Form.Item>
            </Form>
        </Modal>

        <Modal
            title={editingDocument ? 'Editar tipo de documento' : 'Nuevo tipo de documento'}
            open={documentModalOpen}
            width={1100}
            style={{ top: 24 }}
            onCancel={() => setDocumentModalOpen(false)}
            onOk={saveDocument}
            okText="Guardar"
            cancelText="Cancelar"
        >
            <Form layout="vertical">
                <Form.Item label="Nombre" required>
                    <Input value={documentName} maxLength={255} onChange={event => setDocumentName(event.target.value)} />
                </Form.Item>
                <Form.Item label="Codigo" required>
                    <Input value={documentCode} maxLength={125} onChange={event => setDocumentCode(event.target.value)} />
                </Form.Item>
                <Form.Item label="Plantilla HTML">
                    <Alert
                        type="info"
                        showIcon
                        message="Diseñe una plantilla digital reutilizable"
                        description="Los campos {{...}} son datos automáticos de la ficha médica y la atención (paciente, diagnóstico, órdenes, signos vitales, médico y procedimiento); el backend los toma como una instantánea de MS Medical Record. Los campos data-field son parámetros editables que el médico completa durante el procedimiento. Los diagnósticos no se editan en el informe. La vista previa utiliza únicamente datos DEMO y no los guarda."
                        style={{ marginBottom: 12 }}
                    />
                    <Tabs
                        defaultActiveKey="html"
                        items={[
                            {
                                key: 'html',
                                label: 'HTML',
                                children: <>
                                    <Space wrap style={{ marginBottom: 10 }}>
                                        <Text type="secondary">Datos automáticos de la ficha médica:</Text>
                                        {!automaticTemplateTags.length && <Text type="secondary">No hay tags activos configurados.</Text>}
                                        {automaticTemplateTags.map(item => (
                                            <Button
                                                key={item.code}
                                                size="small"
                                                title={item.category ? `Categoría: ${item.category}` : undefined}
                                                onClick={() => insertTemplateText(String(item.token || `{{${item.code}}}`))}
                                            >
                                                {item.label}
                                            </Button>
                                        ))}
                                    </Space>
                                    <Space wrap style={{ marginBottom: 10 }}>
                                        <Text type="secondary">Campos que completa el médico:</Text>
                                        {!editableTemplateTags.length && <Text type="secondary">No hay campos editables activos configurados.</Text>}
                                        {editableTemplateTags.map(item => (
                                            <Button
                                                key={item.code}
                                                size="small"
                                                title={item.category ? `Categoría: ${item.category}` : undefined}
                                                onClick={() => insertTemplateText(`<div data-field="${item.code}"></div>`)}
                                            >
                                                {item.label}
                                            </Button>
                                        ))}
                                        <Button size="small" onClick={() => setDocumentTemplate(PROCEDURE_REPORT_TEMPLATE)}>Cargar informe de procedimiento</Button>
                                        <Button size="small" onClick={() => setDocumentTemplate(TEMPLATE_EXAMPLE)}>Cargar ejemplo de laboratorio</Button>
                                    </Space>
                                    <Input.TextArea
                                        ref={templateTextAreaRef}
                                        value={documentTemplate}
                                        rows={20}
                                        spellCheck={false}
                                        onChange={event => setDocumentTemplate(event.target.value)}
                                        placeholder="Pegue aquí el HTML de la plantilla..."
                                    />
                                </>
                            },
                            {
                                key: 'preview',
                                label: 'Vista previa',
                                children: <iframe
                                    title="Vista previa de plantilla HTML"
                                    sandbox=""
                                    srcDoc={renderTemplatePreview(documentTemplate)}
                                    style={{ width: '100%', height: 560, border: '1px solid #d9d9d9', borderRadius: 6, background: '#fff' }}
                                />
                            }
                        ]}
                    />
                </Form.Item>
            </Form>
        </Modal>
    </>;
};

export default connect(null, {
    deleteGlobalDocumentType,
    getGlobalDocumentTypes,
    getProcedureDocumentTemplateTags,
    getGlobalPhysicalSpaceTypes,
    inactivateGlobalPhysicalSpaceType,
    postGlobalDocumentType,
    postGlobalPhysicalSpaceType,
    putGlobalDocumentType,
    putGlobalPhysicalSpaceType
})(GlobalOrganizationConfiguration);
